'use client';

// Panel / GESTOR DOCUMENTAL del expediente. Lista los documentos cargados
// (GET /expedientes/:id/documentos) con su carpeta, nombre, versión y fecha, y
// permite descargar cada uno (proxy autenticado con el storage_key). El botón
// "Subir documento" abre un diálogo con CampoPDF + Select de carpeta + nombre y
// recarga la lista al terminar. Español; errores 422 como { message } → toast.

import { useCallback, useEffect, useState } from 'react';
import { Download, FolderOpen, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  listarDocumentos,
  descargarDocumento,
  etiquetaCarpeta,
  CARPETAS,
  CARPETA_DOCUMENTAL,
  type CarpetaDocumental,
  type DocumentoExpediente,
} from '@/lib/documentos';
import CampoPDF from '@/components/CampoPDF';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

export default function PanelDocumentos({
  expedienteId, onRefresh,
}: {
  expedienteId: string; onRefresh?: () => void;
}) {
  const [items, setItems] = useState<DocumentoExpediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [subir, setSubir] = useState(false);
  const [descargando, setDescargando] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await listarDocumentos(expedienteId);
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.error('No se pudieron cargar los documentos', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setCargando(false);
    }
  }, [expedienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  function refrescar() { cargar(); onRefresh?.(); }

  async function descargar(doc: DocumentoExpediente) {
    setDescargando(doc.id);
    try {
      await descargarDocumento(doc.storage_key, doc.nombre);
    } catch (e) {
      toast.error('No se pudo descargar el documento', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setDescargando(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="size-5 text-primary" /> Gestor documental
          </CardTitle>
          <Button size="sm" onClick={() => setSubir(true)}>
            <Upload className="size-4" /> Subir documento
          </Button>
        </CardHeader>
        <CardContent>
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Carpeta</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Versión</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Badge variant="secondary">{etiquetaCarpeta(d.carpeta)}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[18rem] truncate font-medium">
                      {d.nombre || '—'}
                    </TableCell>
                    <TableCell>{d.tipo_documento || '—'}</TableCell>
                    <TableCell>{d.version ?? '—'}</TableCell>
                    <TableCell>{fmtFecha(d.fecha_carga)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={descargando === d.id}
                        onClick={() => descargar(d)}
                      >
                        {descargando === d.id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Download className="size-3.5" />
                        )}
                        Descargar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Sin documentos cargados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {subir && (
        <ModalSubirDocumento
          expedienteId={expedienteId}
          onClose={() => setSubir(false)}
          onListo={() => { setSubir(false); refrescar(); }}
        />
      )}
    </div>
  );
}

// ---- Modal: subir documento (CampoPDF + carpeta + nombre) ------------------

function ModalSubirDocumento({ expedienteId, onClose, onListo }: {
  expedienteId: string; onClose: () => void; onListo: () => void;
}) {
  const [carpeta, setCarpeta] = useState<CarpetaDocumental>('ANEXOS');
  const [nombre, setNombre] = useState('');
  const [subido, setSubido] = useState(false);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="size-5 text-primary" /> Subir documento
          </DialogTitle>
          <DialogDescription>
            Adjunte un documento al expediente. Se guarda en el gestor documental.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Carpeta</Label>
            <Select value={carpeta} onValueChange={(v) => setCarpeta(v as CarpetaDocumental)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CARPETAS.map((c) => (
                  <SelectItem key={c} value={c}>{CARPETA_DOCUMENTAL[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="nom-doc">Nombre (opcional)</Label>
            <Input
              id="nom-doc"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Auto de apertura"
            />
          </div>
          <CampoPDF
            label="Archivo"
            carpeta={carpeta}
            expedienteId={expedienteId}
            nombre={nombre.trim() || undefined}
            onChange={(path) => setSubido(!!path)}
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="button" disabled={!subido} onClick={onListo}>Listo</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
